/** Extended MediaControlBar that auto-hides low-priority controls on small containers. */

import { MediaControlBar } from 'media-chrome';

/** Control priorities - higher values stay visible longer. */
const CONTROL_PRIORITIES: Record<string, number> = {
  'media-play-button': 100,
  'media-mute-button': 90,
  'media-fullscreen-button': 85,
  'bunny-settings-menu-button': 80,
  'media-time-display': 70,
  'bunny-time-display': 70,
  'media-airplay-button': 65,
  'media-cast-button': 65,
  'media-pip-button': 60,
  'media-volume-range': 40,
  'media-captions-button': 35,
  'media-seek-backward-button': 30,
  'media-seek-forward-button': 30,
};

const DEFAULT_PRIORITY = 50;

interface HiddenControl {
  element: HTMLElement;
  hiddenAtWidth: number;
}

class BunnyMediaControlBar extends MediaControlBar {
  #resizeObserver: ResizeObserver | null = null;
  #hiddenControls: HiddenControl[] = [];
  #adjustTimeout: number | null = null;
  #isAdjusting = false;

  override connectedCallback() {
    super.connectedCallback?.();

    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    this.#resizeObserver = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? this.clientWidth;
      if (!width || !isFinite(width) || width <= 0) return;

      this.#scheduleAdjust(width);
    });
    this.#resizeObserver.observe(this);

    requestAnimationFrame(() => {
      const width = this.clientWidth;
      if (width > 0) this.#adjust(width);
    });
  }

  override disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    if (this.#adjustTimeout !== null) {
      clearTimeout(this.#adjustTimeout);
      this.#adjustTimeout = null;
    }
  }

  #scheduleAdjust(width: number) {
    if (this.#adjustTimeout !== null) {
      clearTimeout(this.#adjustTimeout);
    }
    this.#adjustTimeout = setTimeout(() => {
      this.#adjustTimeout = null;
      this.#adjust(width);
    }, 16) as unknown as number;
  }

  #getPriority(el: Element): number {
    return CONTROL_PRIORITIES[el.tagName.toLowerCase()] ?? DEFAULT_PRIORITY;
  }

  #isControlElement(el: Element): el is HTMLElement {
    if (!(el instanceof HTMLElement)) return false;
    if (el.classList.contains('spacer')) return false;
    if (el.tagName === 'DIV' || el.tagName === 'SLOT') return false;
    return true;
  }

  #isSpacer(el: Element): boolean {
    return el instanceof HTMLElement && el.classList.contains('spacer');
  }

  /** Calculate minimum content width. Can't use scrollWidth - flexbox shrinks to fit. */
  #getContentWidth(
    gap: number,
    paddingLeft: number,
    paddingRight: number,
  ): number {
    let totalWidth = 0;
    // Count ALL visible flex items (including spacers) for accurate gap calculation.
    // CSS flexbox gap applies between all adjacent flex items, not just controls.
    let flexItemCount = 0;

    for (const child of this.children) {
      if (!(child instanceof HTMLElement)) continue;

      // Use offsetWidth/offsetHeight check - elements with display:none have 0 dimensions
      // This avoids expensive getComputedStyle calls
      if (child.offsetWidth === 0 && child.offsetHeight === 0) continue;

      flexItemCount++;

      // Spacers participate in gap calculation but their width is intentionally
      // ignored (they use flex-grow to fill available space).
      if (this.#isSpacer(child)) continue;

      totalWidth += child.offsetWidth;
    }

    if (flexItemCount === 0) return 0;

    if (flexItemCount > 1) {
      totalWidth += gap * (flexItemCount - 1);
    }

    totalWidth += paddingLeft + paddingRight;

    return totalWidth;
  }

  #isOverflowing(
    gap: number,
    paddingLeft: number,
    paddingRight: number,
  ): boolean {
    return this.#getContentWidth(gap, paddingLeft, paddingRight) >
      this.clientWidth;
  }

  #getVisibleControls(): HTMLElement[] {
    const hiddenSet = new Set(this.#hiddenControls.map((h) => h.element));

    return [...this.children].filter((el): el is HTMLElement => {
      if (!this.#isControlElement(el)) return false;
      if (hiddenSet.has(el)) return false;
      // Avoid getComputedStyle - elements with display:none have 0 dimensions
      return !(el.offsetWidth === 0 && el.offsetHeight === 0);
    });
  }

  #adjust(containerWidth: number) {
    if (this.#isAdjusting || !this.isConnected) return;

    this.#isAdjusting = true;
    try {
      // Cache computed styles once per adjust cycle to avoid repeated getComputedStyle calls
      const computedStyle = getComputedStyle(this);
      const gap = parseFloat(computedStyle.gap) || 0;
      const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0;
      const paddingRight = parseFloat(computedStyle.paddingRight) || 0;

      this.#cleanupStaleReferences();
      this.#tryRestore(containerWidth, gap, paddingLeft, paddingRight);
      this.#hideUntilFits(containerWidth, gap, paddingLeft, paddingRight);
    } finally {
      this.#isAdjusting = false;
    }
  }

  #cleanupStaleReferences() {
    this.#hiddenControls = this.#hiddenControls.filter(
      ({ element }) => element.isConnected && this.contains(element),
    );
  }

  /**
   * Attempts to restore previously hidden controls when container width increases.
   * Controls are restored in order of the width at which they were hidden (largest first),
   * meaning controls hidden at wider widths get priority for restoration.
   */
  #tryRestore(
    containerWidth: number,
    gap: number,
    paddingLeft: number,
    paddingRight: number,
  ) {
    if (this.#hiddenControls.length === 0) return;

    const candidates = [...this.#hiddenControls].sort(
      (a, b) => b.hiddenAtWidth - a.hiddenAtWidth,
    );

    for (const { element, hiddenAtWidth } of candidates) {
      if (!element.isConnected) continue;

      // 10px hysteresis prevents flickering when width hovers near the threshold.
      // Without this buffer, controls would rapidly show/hide at boundary widths.
      if (containerWidth > hiddenAtWidth + 10) {
        element.style.display = '';
        this.#hiddenControls = this.#hiddenControls.filter(
          (h) => h.element !== element,
        );

        if (this.#isOverflowing(gap, paddingLeft, paddingRight)) {
          element.style.display = 'none';
          this.#hiddenControls.push({ element, hiddenAtWidth: containerWidth });
          break;
        }
      }
    }
  }

  /**
   * Hides controls until content fits within the container width.
   * Controls are hidden in priority order (lowest priority first).
   * Lower priority values = hidden first (e.g., seek buttons at 30 hide before play at 100).
   */
  #hideUntilFits(
    containerWidth: number,
    gap: number,
    paddingLeft: number,
    paddingRight: number,
  ) {
    const visible = this.#getVisibleControls()
      .map((el) => ({ el, priority: this.#getPriority(el) }))
      .sort((a, b) => a.priority - b.priority);

    // maxIterations prevents infinite loops if isOverflowing() has edge case bugs.
    const maxIterations = visible.length;
    let iterations = 0;

    // Keep at least one control visible (visible.length > 1) so the control bar
    // is never completely empty, even if overflow persists at very small widths.
    while (
      this.#isOverflowing(gap, paddingLeft, paddingRight) &&
      visible.length > 1 && iterations < maxIterations
    ) {
      const toHide = visible.shift()!;
      toHide.el.style.display = 'none';
      this.#hiddenControls.push({
        element: toHide.el,
        hiddenAtWidth: containerWidth,
      });
      iterations++;
    }
  }
}

if (!globalThis.customElements.get('bunny-media-control-bar')) {
  globalThis.customElements.define(
    'bunny-media-control-bar',
    BunnyMediaControlBar,
  );
}

export { BunnyMediaControlBar, CONTROL_PRIORITIES };
